import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, csCZ } from "@mui/x-data-grid";
import { CodeListImportJobDto, ImportJobStatus } from "Infrastructure/Api/Api";

interface ImportHistoryProps {
	jobs: CodeListImportJobDto[];
	totalCount: number;
	page: number;
	pageSize: number;
	isLoading: boolean;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
}

const isPartialSuccess = (row: CodeListImportJobDto): boolean =>
	!row.errorMessage && row.definitionsSkipped > 0;

const isFullSuccess = (row: CodeListImportJobDto): boolean =>
	!row.errorMessage && row.definitionsSkipped === 0;

const getStatusIcon = (row: CodeListImportJobDto): React.ReactElement => {
	if (row.status === ImportJobStatus.Pending) {
		return <HourglassEmptyIcon fontSize="small" />;
	}
	if (row.status === ImportJobStatus.Running) {
		return <SyncIcon fontSize="small" />;
	}
	if (row.status === ImportJobStatus.Cancelled) {
		return <CancelIcon fontSize="small" />;
	}
	// For Completed or Failed status, check actual outcome
	if (isPartialSuccess(row)) {
		return <WarningIcon fontSize="small" />;
	}
	if (isFullSuccess(row)) {
		return <CheckCircleIcon fontSize="small" />;
	}
	return <ErrorIcon fontSize="small" />;
};

const getStatusColor = (
	row: CodeListImportJobDto,
): "default" | "primary" | "success" | "error" | "warning" => {
	if (row.status === ImportJobStatus.Pending) {
		return "default";
	}
	if (row.status === ImportJobStatus.Running) {
		return "primary";
	}
	if (row.status === ImportJobStatus.Cancelled) {
		return "warning";
	}
	// For Completed or Failed status, check actual outcome
	if (isPartialSuccess(row)) {
		return "warning";
	}
	if (isFullSuccess(row)) {
		return "success";
	}
	return "error";
};

const getStatusLabel = (row: CodeListImportJobDto): string => {
	if (row.status === ImportJobStatus.Pending) {
		return "Čeká";
	}
	if (row.status === ImportJobStatus.Running) {
		return "Běží";
	}
	if (row.status === ImportJobStatus.Cancelled) {
		return "Zrušeno";
	}
	// For Completed or Failed status, check actual outcome
	if (isPartialSuccess(row) || isFullSuccess(row)) {
		return "Dokončeno";
	}
	return "Chyba";
};

const formatDate = (dateString: string) => {
	const date = new Date(dateString);
	return date.toLocaleString("cs-CZ", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatDuration = (duration: string | null | undefined): string => {
	if (!duration) return "-";

	const parts = duration.split(":");
	if (parts.length < 3) return duration;

	const hours = Number.parseInt(parts[0], 10);
	const minutes = Number.parseInt(parts[1], 10);
	const secondsWithMs = parts[2];
	const seconds = Math.round(Number.parseFloat(secondsWithMs));

	const result: string[] = [];
	if (hours > 0) result.push(`${hours}h`);
	if (minutes > 0 || hours > 0) result.push(`${minutes}m`);
	result.push(`${seconds}s`);

	return result.join(" ");
};

const columns: GridColDef[] = [
	{
		field: "targetEnvironment",
		headerName: "Prostředí",
		flex: 1,
		minWidth: 100,
		sortable: false,
		renderCell: (params) => (
			<Typography variant="body2" fontWeight="medium">
				{params.value}
			</Typography>
		),
	},
	{
		field: "localization",
		headerName: "Lokalizace",
		width: 100,
		sortable: false,
		renderCell: (params) => (
			<Chip label={params.value} size="small" variant="outlined" />
		),
	},
	{
		field: "status",
		headerName: "Status",
		width: 140,
		sortable: false,
		renderCell: (params) => {
			const row = params.row as CodeListImportJobDto;
			const color = getStatusColor(row);
			return (
				<Tooltip
					title={row.errorMessage || ""}
					disableHoverListener={!row.errorMessage}
				>
					<Chip
						icon={getStatusIcon(row)}
						label={getStatusLabel(row)}
						size="small"
						color={color}
						sx={
							color === "warning"
								? {
										backgroundColor: "#f9a825",
										color: "white",
										"& .MuiChip-icon": { color: "white" },
									}
								: undefined
						}
					/>
				</Tooltip>
			);
		},
	},
	{
		field: "startedAt",
		headerName: "Zahájeno",
		width: 150,
		sortable: false,
		renderCell: (params) => (
			<Typography variant="body2">
				{formatDate(params.value as string)}
			</Typography>
		),
	},
	{
		field: "duration",
		headerName: "Trvání",
		width: 80,
		sortable: false,
		renderCell: (params) => (
			<Typography variant="body2" color="text.secondary">
				{formatDuration(params.value as string | null)}
			</Typography>
		),
	},
	{
		field: "definitionsProcessed",
		headerName: "Zpracováno",
		width: 115,
		align: "right",
		headerAlign: "right",
		sortable: false,
		renderCell: (params) => (
			<Typography variant="body2">{params.value}</Typography>
		),
	},
	{
		field: "definitionsSkipped",
		headerName: "Přeskočeno",
		width: 115,
		align: "right",
		headerAlign: "right",
		sortable: false,
		renderCell: (params) => (
			<Typography
				variant="body2"
				color={(params.value as number) > 0 ? "warning.main" : undefined}
			>
				{params.value}
			</Typography>
		),
	},
	{
		field: "startedByName",
		headerName: "Spustil",
		flex: 1,
		minWidth: 120,
		sortable: false,
		renderCell: (params) => {
			const row = params.row as CodeListImportJobDto;
			return (
				<Typography variant="body2" color="text.secondary">
					{params.value || row.startedBy}
				</Typography>
			);
		},
	},
];

export const ImportHistory: React.FunctionComponent<ImportHistoryProps> = ({
	jobs,
	totalCount,
	page,
	pageSize,
	isLoading,
	onPageChange,
	onPageSizeChange,
}) => {
	if (!isLoading && jobs.length === 0 && page === 0) {
		return (
			<Box sx={{ textAlign: "center", py: 4 }}>
				<Typography color="text.secondary">
					Zatím nebyly provedeny žádné importy.
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: 454,
				width: "100%",
				"& .MuiDataGrid-root": {
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 1,
					boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
				},
				"& .MuiDataGrid-columnHeaders": {
					backgroundColor: "white",
					borderBottom: "1px solid",
					borderColor: "divider",
				},
				"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
					outline: "none",
				},
				"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
					outline: "none",
				},
				"& .MuiDataGrid-columnHeaderTitle": {
					fontWeight: 700,
				},
				"& .MuiDataGrid-row--failed": {
					backgroundColor: "error.50",
					"&:hover": {
						backgroundColor: "error.100",
					},
				},
			}}
		>
			<DataGrid
				rows={jobs}
				columns={columns}
				getRowId={(row) => row.codeListImportJobID}
				getRowClassName={(params) => {
					const row = params.row as CodeListImportJobDto;
					if (row.errorMessage) {
						return "MuiDataGrid-row--failed";
					}
					return "";
				}}
				loading={isLoading}
				paginationMode="server"
				rowCount={totalCount}
				page={page}
				pageSize={pageSize}
				onPageChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
				rowsPerPageOptions={[10, 25, 50]}
				disableSelectionOnClick
				disableColumnMenu
				autoHeight={false}
				density="compact"
				localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
			/>
		</Box>
	);
};
